import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import { useApi } from "../../shared/api/index";
import { useNotification } from "~hooks/useNotification";
import { INVALIDATE_PERMISSIONS_SCOPE_CACHE_KEY } from "~constants/permissions";

export const useFetchCompetition = () => {
  const { fetchData } = useApi();
  const { organizationId, sport, competitionId } = useParams();

  return useQuery({
    queryKey: ["competitions", organizationId, competitionId],
    queryFn: async () => {
      const result = await fetchData({
        url: `${sport}/o/${organizationId}/competitions/${competitionId}`,
      });

      return result?.data?.[0];
    },
    enabled: !!competitionId,
  });
};

export const useDeleteCompetition = () => {
  const { sport, organizationId } = useParams();
  const queryClient = useQueryClient();
  const { mutateData } = useApi();
  const { formatMessage } = useIntl();
  const notification = useNotification();

  return useMutation({
    mutationFn: async (competitionId) => {
      await mutateData({
        url: `${sport}/o/${organizationId}/competitions/${competitionId}`,
        method: "DELETE",
      });
    },
    onSuccess: async () => {
      notification.success({
        message: formatMessage({
          id: "competition.delete.success",
          defaultMessage: "Competition deleted successfully",
        }),
        placement: "bottomRight",
      });

      localStorage.setItem(INVALIDATE_PERMISSIONS_SCOPE_CACHE_KEY, "true");

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["competitions"] }),
        queryClient.invalidateQueries({ queryKey: ["permission-scope-current-user"] }),
      ]);
    },
  });
};

export const useCreateCompetition = () => {
  const { sport, organizationId } = useParams();
  const queryClient = useQueryClient();
  const { mutateData } = useApi();
  const { formatMessage } = useIntl();
  const notification = useNotification();

  return useMutation({
    mutationFn: async (data) => {
      await mutateData({
        url: `${sport}/o/${organizationId}/competitions`,
        method: "POST",
        data,
      });
    },
    onSuccess: async () => {
      notification.success({
        message: formatMessage({
          id: "competition.create.success",
          defaultMessage: "Competition created successfully",
        }),
        placement: "bottomRight",
      });

      localStorage.setItem(INVALIDATE_PERMISSIONS_SCOPE_CACHE_KEY, "true");

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["competitions"] }),
        queryClient.invalidateQueries({ queryKey: ["permission-scope-current-user"] }),
      ]);
    },
  });
};

export const useUpdateCompetition = () => {
  const { sport, organizationId, competitionId } = useParams();
  const queryClient = useQueryClient();
  const { mutateData } = useApi();
  const { formatMessage } = useIntl();
  const notification = useNotification();

  return useMutation({
    mutationFn: async (data) => {
      await mutateData({
        url: `${sport}/o/${organizationId}/competitions/${competitionId}`,
        method: "PUT",
        data,
      });
    },
    onSuccess: async () => {
      notification.success({
        message: formatMessage({
          id: "competition.update.success",
          defaultMessage: "Competition updated successfully",
        }),
        placement: "bottomRight",
      });

      localStorage.setItem(INVALIDATE_PERMISSIONS_SCOPE_CACHE_KEY, "true");

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["competitions"] }),
        queryClient.invalidateQueries({ queryKey: ["permission-scope-current-user"] }),
      ]);
    },
  });
};
